import { useState, useEffect } from "react";
// material
import {
  Box,
  Card,
  Typography,
  TableCell,
  TableBody,
  TableContainer,
  Table,
  TableRow,
  Button,
  Stack,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
} from "@mui/material";
import ConfirmDialog from "../../components/ConfirmDialog";
// components
import {
  Log,
  sendDataWithGet,
  sendDataWithPost,
} from "../../services/services";

import ListHead from "../../components/table/ListHead";
import Scrollbar from "../../components/table/Scrollbar";
import { applySortFilter, isEmptyFilter } from "../../utils/tableFunctions";
import { StyledTableRow } from "../../components/TableRow";
import { isEmpty } from "lodash";

import FormSearch from "../../components/FormSearch";
import CircularLoader from "../../components/circularLoader";
import SearchPrompt from "../../components/SearchPrompt";
import { CustomTableCell } from "../../components/TableCell";
import Pagination from "../../components/Pagination";
import { Add, Edit, MoreVert } from "@mui/icons-material";
import { styles } from "../../utils/styles";
import CreateDialog from "../../components/Merchant-payout-info/Dialogs/CreateDialog";
import { useLoadingSpinnerContext } from "../../context/loadingSpinnerContext";
import Label from "../../components/Label";
import { showSuccessMsg } from "../../utils/toast";

const ActionMenu = ({ row, refreshList, setGlobalLoading }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleConfirmOpen = (event) => {
    if (event) event.stopPropagation();
    setOpenConfirm(true);
    handleClose();
  };

  const handleConfirmClose = (event) => {
    if (event) event.stopPropagation();
    setOpenConfirm(false);
  };

  const handleStatusUpdate = async (event) => {
    if (event) event.stopPropagation();
    setLoading(true);
    const url = `/bank_account/merchant_payout_info/update_status/`;
    const payload = {
      id: row.id,
      is_active: !row.is_active,
    };
    try {
      await sendDataWithPost(url, payload, null, null, () => {
        showSuccessMsg("Status update request submitted for approval.");
        refreshList();
        setLoading(false);
        handleConfirmClose();
      }, () => {
        setLoading(false);
      });
    } catch (error) {
      console.error("Status update error:", error);
    }
  };

  return (
    <>
      <Tooltip title="More Options" placement="top">
        <IconButton onClick={handleClick}>
          <MoreVert />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <MenuItem onClick={handleConfirmOpen}>
          {row.is_active ? "Set Inactive" : "Set Active"}
        </MenuItem>
      </Menu>

      <Box onClick={(e) => e.stopPropagation()}>
        <ConfirmDialog
          open={openConfirm}
          onClose={handleConfirmClose}
          onSubmit={handleStatusUpdate}
          loading={loading}
          title="Confirm Status Change"
        >
          <Typography>
            Are you sure you want to set this Merchant Payout Info to {row.is_active ? "Inactive" : "Active"}?
          </Typography>
        </ConfirmDialog>
      </Box>
    </>
  );
};
// ----------------------------------------------------------------------

const defaultValue = { is_active: true };

const TABLE_HEAD = [
  { id: "merchant_name", label: "Merchant" },
  { id: "terminal_id", label: "Terminal ID" },
  { id: "payment_brand_name", label: "Payment Brand" },
  { id: "payment_host_name", label: "Payment Host" },
  { id: "payment_cycle", label: "Payment Cycle" },
  // { id: "transaction_fee_amount", label: "Transaction Fee Amount" },
  { id: "is_active", label: "Active" },
  { id: "action" },
];

export default function MerchantPayoutDetail({ userRole }) {
  const { setGlobalLoading } = useLoadingSpinnerContext();
  const [loaded, setLoaded] = useState(false);
  const [tableData, setTableData] = useState({
    results: [],
  });
  const [filter, setFilter] = useState(defaultValue);

  const [fLoaded, setFLoaded] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [filterCondition, setFilterCondition] = useState({});
  const [mode, setMode] = useState("edit");
  // the list is only queried once the user has searched, so the tab opens fast
  const [searched, setSearched] = useState(false);

  // ------------------ filterStuffies --------------------//
  const [pagination, setPagination] = useState({ page: 1, page_size: 50 });

  //Dialogs
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("name");

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    queryPage(
      {
        page: newPage + 1,
        page_size: pagination.page_size,
      },
      filter
    );
  };

  const handleChangeRowsPerPage = (event) => {
    queryPage(
      {
        page: 1,
        page_size: event.target.value,
      },
      filter
    );
  };

  const renderData = (res) => {
    const newData = res.results.map((item) => ({
      ...item,
      merchant_name: item.merchant?.name,
      terminal_id: item.terminal?.terminal_id,
      payment_brand_name: item?.payment_brand?.name,
      payment_host_name: item?.payment_host?.name,
    }));

    setTableData({
      ...res,
      results: newData,
    });
  };

  const queryPage = (params, payload = filter) => {
    loaded && setLoaded(false);
    const queryParams = new URLSearchParams(params || pagination).toString();
    const url = `/bank_account/merchant_payout_info/filter/?${queryParams}`;
    sendDataWithPost(url, payload, renderData, setLoaded, () => {
      params && setPagination(params);
    });
  };

  const getFilterCondition = () => {
    const url = `/bank_account/merchant_payout_info/filter_conditions/`;
    sendDataWithGet(url, setFilterCondition, setFLoaded);
  };

  const onFilterSubmit = (params) => {
    // searching with an empty form falls back to the filter the tab used to
    // load with on first render
    const fParams = isEmptyFilter(params) ? { ...defaultValue } : params;
    const newParams = {
      ...fParams,
      merchant_id: fParams?.merchant?.id,
      terminal_id: fParams?.terminal?.id,
      payment_host_id: fParams?.payment_host?.id,
      payment_cycle: fParams?.payment_cycle?.id,
    };

    if (newParams.is_active === "all") delete newParams.is_active;

    const newPagination = {
      ...pagination,
      page: 1,
    };

    Log.info("Search Merchant Payout Info list", newParams);
    setSearched(true);
    setFilter(newParams);
    queryPage(newPagination, newParams);
  };

  const handleCloseDialog = () => {
    openCreateDialog && setOpenCreateDialog(null);
  };

  // ------------------ end of filter stuffies --------------------//

  useEffect(() => {
    getFilterCondition();
  }, []);

  const isUserNotFound = tableData?.results?.length === 0;

  const filteredList = applySortFilter(
    tableData?.results || [],
    order,
    orderBy
  );

  const schema = [
    {
      type: "search-select",
      label: "Merchant",
      param: "merchant",
      options: filterCondition?.merchant?.map((item) => ({
        label: item.merchant__name,
        id: item.merchant__id,
      })),
    },
    {
      type: "search-select",
      label: "Terminal",
      param: "terminal",
      options: filterCondition?.terminal?.map((item) => ({
        label: item.terminal__terminal_id,
        id: item.terminal__id,
      })),
    },
    {
      type: "search-select",
      label: "Payment Host",
      param: "payment_host",
      options: filterCondition?.payment_host?.map((item) => ({
        label: item.payment_host__name,
        id: item.payment_host__id,
      })),
    },
    {
      type: "search-select",
      label: "Payment Cycle",
      param: "payment_cycle",
      options: filterCondition?.payment_cycle?.map((item) => ({
        label: item.name,
        id: item.id,
      })),
    },
    {
      type: "select",
      label: "Status",
      param: "is_active",
      options: [
        { id: "all", name: "All" },
        { id: true, name: "Active" },
        { id: false, name: "Inactive" },
      ],
    },
  ];

  return (
    <Box>
      {userRole.includes("Add") && (
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          mb={2}
        >
          <Button
            variant="contained"
            onClick={() => setOpenCreateDialog(true)}
            startIcon={<Add />}
            sx={{ ...styles.customButton }}
          >
            Add Merchant Payout Info
          </Button>
        </Stack>
      )}

      <FormSearch
        schema={schema}
        loading={fLoaded}
        onSearch={onFilterSubmit}
        defaultValue={defaultValue}
      />
      {!searched ? (
        <SearchPrompt />
      ) : loaded ? (
        <Card>
          <Scrollbar>
            <TableContainer>
              <Table>
                <ListHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                  headLabel={TABLE_HEAD}
                  checkbox={false}
                />
                <TableBody>
                  {filteredList?.map((row) => {
                    const {
                      id,
                      merchant_name,
                      terminal_id,
                      payment_brand_name,
                      payment_host_name,
                      payment_cycle,
                      // transaction_fee_amount,
                      is_active,
                    } = row;

                    return (
                      <StyledTableRow hover key={id} onClick={() => {
                        const url = `/bank_account/merchant_payout_info/details/${row.id}/`;
                        setGlobalLoading(true);
                        setMode("view");
                        sendDataWithGet(
                          url,
                          setOpenCreateDialog,
                          () => setGlobalLoading(false)
                        );
                      }}>
                        <CustomTableCell field={merchant_name} />
                        <CustomTableCell field={terminal_id} />
                        <CustomTableCell field={payment_brand_name} />
                        <CustomTableCell field={payment_host_name} />
                        <CustomTableCell field={payment_cycle} />
                        {/* <CustomTableCell field={transaction_fee_amount} /> */}
                        <CustomTableCell
                          field={
                            <Label color={is_active ? "success" : "error"}>
                              {is_active ? "YES" : "NO"}
                            </Label>
                          }
                        />
                        <CustomTableCell
                          align="right"
                          field={
                            <>
                              {userRole.includes("Edit") && (
                                <>
                                  <Tooltip title="Edit" placement="top">
                                    <IconButton
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        const url = `/bank_account/merchant_payout_info/details/${row.id}/`;
                                        setGlobalLoading(true);
                                        setMode("edit");
                                        sendDataWithGet(
                                          url,
                                          setOpenCreateDialog,
                                          () => setGlobalLoading(false)
                                        );
                                      }}
                                    >
                                      <Edit />
                                    </IconButton>
                                  </Tooltip>
                                  <ActionMenu 
                                    row={row} 
                                    refreshList={() => queryPage(pagination, filter)} 
                                    setGlobalLoading={setGlobalLoading} 
                                  />
                                </>
                              )}
                            </>
                          }
                        />
                      </StyledTableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={9} sx={{ py: 3 }}>
                        <Typography
                          gutterBottom
                          align="center"
                          variant="subtitle1"
                        >
                          Not found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>
          {!isEmpty(tableData?.results) && (
            <Pagination
              count={tableData.count}
              pagination={pagination}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </Card>
      ) : (
        <CircularLoader />
      )}

      {openCreateDialog && (
        <CreateDialog
          data={{
            merchant: filterCondition?.merchant,
            details: openCreateDialog,
            mode: mode
          }}
          onClose={handleCloseDialog}
          cb={() => {
            setSearched(true);
            queryPage(pagination, filter);
          }}
        />
      )}
    </Box>
  );
}
